import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { CustomersService } from '../customers.service'
import { CustomersRepository } from '../customers.repository'
import { CustomerSearchParams } from '../models/customer-search.model'
import { Customer } from '../models/customer.entity'
import {
  CustomersListingData,
  CustomersListingPage,
} from '../models/customer-listing.model'

interface CustomersListingState {
  filters?: CustomerSearchParams
  data?: Customer[]
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
  extData?: CustomersListingData
  searchString?: string
}

const CUSTOMERS_LISTING_INITIAL_STATE: CustomersListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class CustomersListingStore {
  constructor(
    private customersService: CustomersService,
    private customersRepository: CustomersRepository,
  ) {}

  private state = signal<CustomersListingState>(CUSTOMERS_LISTING_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly customers = computed(() => this.state().data || [])
  readonly extData = computed(() => this.state().extData)
  readonly totalCount = computed(() => this.state().totalCount || 0)

  // Effects

  async search(
    params?: CustomerSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.customersRepository
        .searchCustomers$(
          this.parseStateFilterParams(this.state(), params),
          this.state().extData,
          returnAll,
        )
        .subscribe({
          next: (data) => {
            resolve()
            this.setData(data)
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async delete(customer: Customer): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.customersRepository
        .alert('CUSTOMER_DELETION', customer)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.customersService.delete$(customer._id).subscribe({
              next: () => {
                resolve(true)
                this.customersRepository.notify({
                  code: 'CUSTOMER_DELETED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  // Reducers

  setData(data: CustomersListingPage) {
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      extData: data.extData,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: CustomerSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: CustomerSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: CustomerSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof CustomerSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }

  private parseStateFilterParams(
    state: CustomersListingState,
    filters?: CustomerSearchParams,
  ): CustomerSearchParams {
    const params: CustomerSearchParams = filters || {}
    if (state.searchString) {
      params.q = state.searchString
    }

    return params
  }
}
